import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api, { apiError } from '@/lib/api';
import { isProTier, userTierKey } from '@/lib/tier';
import { Footer } from '@/components/Footer';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Sparkles, Crown, Wind, Zap, Coins, Loader2, Info, Clapperboard } from 'lucide-react';
import { toast } from 'sonner';

export default function Pricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState('');

  const userTier = userTierKey(user);              // 'free' | 'pro' | 'elite'
  const isPro = isProTier(user?.subscription_tier);

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get('/billing/config'); setConfig(data); } catch (e) { console.debug('billing config load failed', e); }
    };
    load();
  }, []);

  const proPlan = period === 'monthly' ? 'pro_monthly' : 'pro_yearly';
  const elitePlan = period === 'monthly' ? 'elite_monthly' : 'elite_yearly';
  const proPrice = period === 'monthly' ? '€9,99' : '€99';
  const elitePrice = period === 'monthly' ? '€19,99' : '€199';
  const perPeriod = period === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear');
  const proSub = period === 'monthly' ? t('pricing.proSubMonthly') : t('pricing.proSubYearly');
  const eliteSub = period === 'monthly' ? t('pricing.eliteSubMonthly') : t('pricing.eliteSubYearly');

  const vcPack = config?.video_credit_packs?.video_10;
  const vcCount = vcPack?.video_credits ?? 10;
  const vcPrice = vcPack ? `€${String(vcPack.amount).replace('.', ',')}` : '€9,99';

  const go = (url) => { window.location.href = url; };

  const subscribe = async (plan, key) => {
    if (!user) { sessionStorage.setItem('kiteiq_pending_plan', plan); navigate('/register'); return; }
    setBusy(key);
    try {
      const { data } = await api.post('/billing/subscribe', { plan, origin: window.location.origin });
      if (data.checkout_url) { go(data.checkout_url); return; }
      toast.error(t('pricing.couldNotCheckout'));
      setBusy('');
    } catch (e) {
      toast.error(apiError(e, t('pricing.couldNotCheckout')));
      setBusy('');
    }
  };

  const buyCredits = async () => {
    if (!user) { navigate('/register'); return; }
    setBusy('credits');
    try {
      const { data } = await api.post('/billing/buy-credits', { pack: 'credits_5', origin: window.location.origin });
      if (data.checkout_url) go(data.checkout_url);
    } catch (e) {
      toast.error(apiError(e, t('pricing.couldNotCheckout')));
      setBusy('');
    }
  };

  const buyVideoCredits = async () => {
    if (!user) { navigate('/register'); return; }
    setBusy('video_credits');
    try {
      const { data } = await api.post('/billing/buy-video-credits', { pack: 'video_10', origin: window.location.origin });
      if (data.checkout_url) go(data.checkout_url);
    } catch (e) {
      toast.error(apiError(e, t('pricing.couldNotCheckout')));
      setBusy('');
    }
  };

  // Resume checkout: a logged-out visitor who picked a plan is sent to sign up /
  // log in, then bounced back here — pick the intent back up and open checkout.
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem('kiteiq_pending_plan');
    if (!pending || !/^(pro|elite)_/.test(pending)) return;
    sessionStorage.removeItem('kiteiq_pending_plan');
    const key = pending.startsWith('elite') ? 'elite' : 'pro';
    if (key === 'pro' && isPro) return;          // already has Pro/Elite access
    if (key === 'elite' && userTier === 'elite') return;
    const planName = key === 'elite' ? t('pricing.elite') : t('pricing.pro');
    toast.success(t('pricing.resuming', { plan: planName }));
    subscribe(pending, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const asList = (key) => { const v = t(key, { returnObjects: true }); return Array.isArray(v) ? v : []; };
  const freeList = asList('pricing.freeFeatures');
  const proList = asList('pricing.proFeatures');
  const eliteList = asList('pricing.eliteFeatures');
  const faqList = asList('pricing.faq');

  // Button state per card
  const proLabel = busy === 'pro'
    ? <Loader2 className="w-4 h-4 animate-spin" />
    : userTier === 'pro' ? t('pricing.yourCurrentPlan')
      : userTier === 'elite' ? t('pricing.included')
        : <><Sparkles className="w-4 h-4 mr-2" /> {t('pricing.upgradeToPro')}</>;
  const eliteLabel = busy === 'elite'
    ? <Loader2 className="w-4 h-4 animate-spin" />
    : userTier === 'elite' ? t('pricing.yourCurrentPlan')
      : <><Crown className="w-4 h-4 mr-2" /> {t('pricing.upgradeToElite')}</>;

  return (
    <div>
      <Seo
        title={t('pricing.heroTitle')}
        description={t('pricing.heroSub')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Product',
              name: 'KITEIQ Pro',
              description: t('pricing.proDesc') || t('pricing.heroSub'),
              brand: { '@type': 'Brand', name: 'KITEIQ' },
              offers: {
                '@type': 'AggregateOffer',
                priceCurrency: 'EUR',
                lowPrice: '9.99',
                highPrice: '99',
                offerCount: 2,
                url: 'https://kitecoach.app/pricing',
                availability: 'https://schema.org/InStock',
              },
            },
            {
              '@type': 'Product',
              name: 'KITEIQ Elite',
              description: t('pricing.eliteDesc') || t('pricing.heroSub'),
              brand: { '@type': 'Brand', name: 'KITEIQ' },
              offers: {
                '@type': 'AggregateOffer',
                priceCurrency: 'EUR',
                lowPrice: '19.99',
                highPrice: '199',
                offerCount: 2,
                url: 'https://kitecoach.app/pricing',
                availability: 'https://schema.org/InStock',
              },
            },
          ],
        }}
      />
      {/* HERO — sand editorial section */}
      <section style={{ background: 'var(--kite-sand)' }} className="text-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="label-eyebrow text-[#FF6B35] mb-2 flex items-center gap-2"><Wind className="w-4 h-4" /> {t('pricing.eyebrow')}</div>
            <h1 className="font-display text-5xl sm:text-6xl">{t('pricing.heroTitle')}</h1>
            <p className="mt-4 text-black/70">{t('pricing.heroSub')}</p>
          </div>

          {/* billing period toggle */}
          <div className="mt-8 inline-flex rounded-xl border border-black/10 bg-white/60 p-1" data-testid="pricing-period-toggle">
            {[{ k: 'monthly', l: t('pricing.monthly') }, { k: 'yearly', l: t('pricing.yearly') }].map((o) => (
              <button key={o.k} onClick={() => setPeriod(o.k)} data-testid={`pricing-period-${o.k}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2 ${period === o.k ? 'bg-[#0A0A0A] text-white' : 'text-black/60 hover:text-black'}`}>
                {o.l}
                {o.k === 'yearly' && (
                  <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-md bg-[#2EE59D] text-[#0A0A0A]" data-testid="pricing-save-pill">{t('pricing.saveShort')}</span>
                )}
              </button>
            ))}
          </div>

          {/* plans — 3 tiers */}
          <div className="grid md:grid-cols-3 gap-5 mt-10 items-start">
            {/* FREE */}
            <div className="rounded-2xl p-6 bg-[#0A0A0A] text-white border border-white/10" data-testid="pricing-card-free">
              <h3 className="font-display text-3xl">{t('pricing.free')}</h3>
              <p className="text-sm text-muted-foreground mt-1 h-10">{t('pricing.freeSub')}</p>
              <div className="flex items-end gap-1 mt-3"><span className="font-data text-4xl font-semibold">€0</span><span className="text-muted-foreground mb-1">{t('pricing.forever')}</span></div>
              <Button onClick={() => navigate(user ? '/analyze' : '/register')} disabled={isPro}
                className="w-full mt-5 rounded-xl font-semibold bg-white/10 hover:bg-white/15 text-white disabled:opacity-40" data-testid="pricing-free-button">
                {user ? t('pricing.startAnalyzing') : t('pricing.getStartedFree')}
              </Button>
              <ul className="mt-6 space-y-2.5">
                {freeList.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#2EE59D] mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
            </div>

            {/* PRO — most popular */}
            <div className="rounded-2xl p-6 bg-[#0A0A0A] text-white relative glow-blue ring-1 ring-[#00B4D8] md:-mt-3 md:mb-3" data-testid="pricing-card-pro">
              <span className="absolute -top-3 left-6 label-eyebrow bg-[#00B4D8] text-black px-3 py-1 rounded-full" data-testid="pricing-most-popular">{t('pricing.mostPopular')}</span>
              <h3 className="font-display text-3xl">{t('pricing.pro')}</h3>
              <p className="text-sm text-muted-foreground mt-1 h-10">{t('pricing.proSub')}</p>
              <div className="flex items-end gap-1 mt-3"><span className="font-data text-4xl font-semibold">{proPrice}</span><span className="text-muted-foreground mb-1">{perPeriod}</span></div>
              <div className="text-xs text-[#48CAE4] mt-1 h-4">{proSub}</div>
              {period === 'yearly' && (
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#2EE59D]/15 text-[#2EE59D] border border-[#2EE59D]/30" data-testid="pricing-pro-savings">
                  <Zap className="w-3 h-3" /> {t('pricing.saveYearly')}
                </span>
              )}
              <Button onClick={() => subscribe(proPlan, 'pro')} disabled={isPro || busy === 'pro'}
                className="w-full mt-5 rounded-xl font-semibold bg-[#00B4D8] hover:bg-[#48CAE4] text-black disabled:opacity-50" data-testid="pricing-pro-button">
                {proLabel}
              </Button>
              <ul className="mt-6 space-y-2.5">
                {proList.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#2EE59D] mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
            </div>

            {/* ELITE */}
            <div className="rounded-2xl p-6 bg-[#0A0A0A] text-white relative ring-1 ring-[#FFB020]/60" data-testid="pricing-card-elite">
              <span className="absolute -top-3 left-6 label-eyebrow bg-[#FFB020] text-black px-3 py-1 rounded-full inline-flex items-center gap-1"><Crown className="w-3 h-3" /> {t('pricing.elite')}</span>
              <h3 className="font-display text-3xl">{t('pricing.elite')}</h3>
              <p className="text-sm text-muted-foreground mt-1 h-10">{t('pricing.eliteSub')}</p>
              <div className="flex items-end gap-1 mt-3"><span className="font-data text-4xl font-semibold">{elitePrice}</span><span className="text-muted-foreground mb-1">{perPeriod}</span></div>
              <div className="text-xs text-[#FFD27A] mt-1 h-4">{eliteSub}</div>
              {period === 'yearly' && (
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold px-2 py-1 rounded-md bg-[#2EE59D]/15 text-[#2EE59D] border border-[#2EE59D]/30" data-testid="pricing-elite-savings">
                  <Zap className="w-3 h-3" /> {t('pricing.saveYearly')}
                </span>
              )}
              <Button onClick={() => subscribe(elitePlan, 'elite')} disabled={userTier === 'elite' || busy === 'elite'}
                className="w-full mt-5 rounded-xl font-semibold bg-[#FFB020] hover:bg-[#ffc04d] text-black disabled:opacity-50" data-testid="pricing-elite-button">
                {eliteLabel}
              </Button>
              <ul className="mt-6 space-y-2.5">
                {eliteList.map((f, i) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${i === 0 ? 'text-white/90 font-semibold' : ''}`}>
                    {i === 0 ? <Crown className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" /> : <Check className="w-4 h-4 text-[#2EE59D] mt-0.5 shrink-0" />} {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIT PACKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-2"><Coins className="w-4 h-4" /> {t('pricing.creditPacksEyebrow')}</div>
            <h2 className="font-display text-4xl">{t('pricing.creditPacksTitle')}</h2>
            <p className="text-muted-foreground mt-3 max-w-md">{t('pricing.creditPacksDesc')}</p>
          </div>
          <div className="kite-card p-6 flex items-center justify-between gap-4" data-testid="credit-pack-card">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center"><Zap className="w-7 h-7 text-[#00B4D8]" /></div>
              <div>
                <div className="font-display text-2xl">{t('pricing.fiveCredits')}</div>
                <div className="text-sm text-muted-foreground">{t('pricing.fiveCreditsSub')}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-data text-3xl font-semibold">€4,99</div>
              <Button onClick={buyCredits} disabled={busy === 'credits'} className="mt-2 rounded-xl bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold" data-testid="buy-credits-button">
                {busy === 'credits' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('pricing.buyCredits')}
              </Button>
            </div>
          </div>
        </div>

        {/* Kaia video-credits */}
        <div className="grid lg:grid-cols-2 gap-6 items-center mt-6">
          <div>
            <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-2"><Clapperboard className="w-4 h-4" /> {t('pricing.videoCreditsEyebrow')}</div>
            <h2 className="font-display text-4xl">{t('pricing.videoCreditsTitle')}</h2>
            <p className="text-muted-foreground mt-3 max-w-md">{t('pricing.videoCreditsDesc')}</p>
            {user && (
              <p className="text-xs text-[#48CAE4] mt-2" data-testid="video-credits-balance">
                {t('pricing.videoCreditsBalance', { count: user?.video_credits ?? 0 })}
              </p>
            )}
          </div>
          <div className="kite-card p-6 flex items-center justify-between gap-4" data-testid="video-credit-pack-card">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center"><Clapperboard className="w-7 h-7 text-[#00B4D8]" /></div>
              <div>
                <div className="font-display text-2xl">{t('coach.video.videoCreditsLeft', { count: vcCount })}</div>
                <div className="text-sm text-muted-foreground">{t('pricing.videoCreditsPerVideo')}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-data text-3xl font-semibold">{vcPrice}</div>
              <Button onClick={buyVideoCredits} disabled={busy === 'video_credits'} className="mt-2 rounded-xl bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold" data-testid="buy-video-credits-button">
                {busy === 'video_credits' ? <Loader2 className="w-4 h-4 animate-spin" /> : t('pricing.videoCreditsCta', { count: vcCount })}
              </Button>
            </div>
          </div>
        </div>

        {config && !config.mollie_configured && config.demo_mode && (
          <div className="mt-8 rounded-xl border border-[#48CAE4]/30 bg-[#48CAE4]/5 p-4 flex items-start gap-3 text-sm" data-testid="pricing-demo-notice">
            <Info className="w-5 h-5 text-[#48CAE4] shrink-0" />
            <div className="text-muted-foreground">
              <span className="text-white font-semibold">{t('pricing.demoTitle')}</span> {t('pricing.demoText')}
            </div>
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="font-display text-4xl mb-8">{t('pricing.questions')}</h2>
        <Accordion type="single" collapsible className="w-full">
          {faqList.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left hover:text-[#48CAE4]" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <Footer />
    </div>
  );
}
